import {
  BehaviorSubject,
  catchError,
  combineLatestWith,
  filter,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';

import {
  Container,
  HeartModalSource,
  history,
  Service,
  HeartService,
  StorageService,
  LearningExperienceType,
} from '../../../../symphony';

import { SlApiService } from '../sl-api.service';
import { SlDataService } from '../sl-data.service';
import { SlActions } from '../ActionsService/sl-actions';
import { SlLEContext } from '../../global-constants';
import {
  ActionType,
  RequestType,
  TrackActionName,
} from '../../global-interface';
import { SlNavigationService } from '../sl-navigation.service';
import { SlHeartsService } from './sl-hearts.service';
import { getLEHeaders } from '../../utils/utilsFunctions';
import { SlStorageKeys } from '../../../../shared/public/globalInterfaces/globalInterfaces';
import { IError, ErrorCodes } from '../../../../api/private/global.interface';
import {
  IMaterialInfo, MaterialType, ISolveMaterialPayload, ISolveMaterialDTO, IMaterialStatus, IMaterialChange, IMaterial, VisibilityStatus, OwnershipAvailabilityType, IMaterialSolutionSubmission,
  ResourceType,
  MaterialRequirementType,
} from '../../../../api/private/learn-engine-api/learn-engine-api.interface';
import { SlErrorHandlerService } from '../../../../api/public/error-handling';
import { getLETypeFromRoute } from '../../utils/helpers';
import { Touchpoint } from '../../../../api/public/subscription-api';
import { DynamicPaywallLoader } from '../../../../_paywalls/public/DynamicPaywallLoader';

@Service()
export class SlMaterialService {
  private api = Container.take(SlLEContext, SlApiService);

  private data = Container.take(SlLEContext, SlDataService);

  private actions$ = Container.take(SlLEContext, SlActions).actions$;

  private heartService = Container.take(SlLEContext, SlHeartsService);

  private heart = Container.take('global', HeartService);

  private storageService = Container.take('global', StorageService);

  private paywallLoader = DynamicPaywallLoader.ensureRegistered();

  private handleError = Container.take(SlLEContext, SlErrorHandlerService)
    .handleError;

  private navigationService = Container.take(
    SlLEContext,
    SlNavigationService,
  );

  private solveMaterialWithBundleId$ = new BehaviorSubject<IMaterialSolutionSubmission[]>(null);

  constructor() {
    const unsubscriber: Subject<void> = Container.take(
      SlLEContext,
      'unsubscriber',
    );

    this.solveMaterialWithBundleId$.pipe(
      filter((d) => !!d),
      combineLatestWith(this.data.learningPath$.pipe(filter((p) => !!p))),
      tap(([solutions, path]) => {
        this.actions$.next({
          type: ActionType.SolveMaterial,
          requestType: RequestType.Cancelable,
          data: {
            solutions,
            id: path.bundleId,
          },
        });
        this.solveMaterialWithBundleId$.next(null);
      }),
    )
      .pipe(takeUntil(unsubscriber))
      .subscribe();
  }

  public trySolveMaterial = (): Observable<null> => {
    const solutions: IMaterialInfo[] = this.data.solutions$.value;
    const unsolvedMaterial: IMaterialSolutionSubmission[] = [];
    solutions.forEach((item) => {
      if (
        item.header.typeId === MaterialType.Theory
        && !item.status?.isCompleted
      ) {
        unsolvedMaterial.push({
          materialRelationId: item.header.materialRelationId,
          typeId: item.header.typeId,
        });
      }
    });
    if (unsolvedMaterial.length > 0) {
      this.actions$.next({
        type: ActionType.SolveMaterial,
        requestType: RequestType.Parallel,
        data: {
          solutions: unsolvedMaterial,
        },
      });
    }
    return of(null);
  };

  private solveMaterialHandleError = (err: IError[]): void => {
    this.handleError(err, {
      [ErrorCodes.BadRequest]: () => {
        this.navigationService.navigation$.next({
          toErrorRoute: true,
        });
      },
      [ErrorCodes.NotFound]: () => {
        this.navigationService.navigation$.next({
          toErrorRoute: true,
        });
      },
      [ErrorCodes.Forbidden]: () => {
        this.navigationService.navigation$.next({
          toErrorRoute: true,
        });
      },
      [ErrorCodes.NotEnoughHearts]: () => {
        this.heartService.openHeartModal(
          HeartModalSource.SpentLastHeart,
        );
      },
      [ErrorCodes.ExpiredSession]: () => {
        this.actions$.next({
          type: ActionType.ExpiredSessionIdError,
          requestType: RequestType.Parallel,
        });
      },
    });
  };

  public solveMaterial = (
    payload: ISolveMaterialPayload,
  ): Observable<ISolveMaterialDTO> => {
    if (!payload.bundleId && getLETypeFromRoute(this.data.routeData$.value) === LearningExperienceType.LearningPath) {
      if (this.data.learningPath$.value) {
        payload.bundleId = this.data.learningPath$.value.bundleId;
      } else {
        this.solveMaterialWithBundleId$.next(payload.solutions);
        return of(null);
      }
    }

    const { loading$, disabledActions$ } = this.data;
    loading$.next({
      solveMaterial: true,
      footerPrimaryButton: true,
    });
    disabledActions$.next({
      question: true,
    });

    const session: string = this.storageService.load(
      SlStorageKeys.Sl_LE_SESSION,
      'session',
    );
    const headers = getLEHeaders();

    if (this.data.isSkipAhead.value) {
      payload.resourceType = ResourceType.Dynamic;
    }

    return this.api.solveMaterial(payload, session, headers, this.data.isSkipAhead.value).pipe(
      tap((data) => {
        this.solveMaterialCallback(data);
        this.data.solutions$.next([]);
        loading$.next(null);
      }),
      catchError((err) => {
        this.solveMaterialHandleError(err);
        this.data.solutions$.next([]);

        loading$.next(null);
        return of(null);
      }),
    );
  };

  private setFailedSolutionsCountValue = (): void => {
    const { question$ } = this.data;
    this.data.failedSolutionsCount = {
      ...this.data.failedSolutionsCount,
      [question$.value.header.materialRelationId]:
        (this.data.failedSolutionsCount?.[question$.value.header.materialRelationId] || 0) + 1,
    };
  };

  public solveMaterialCallback = (
    { solutions, statusChanges, heartsState }: ISolveMaterialDTO,
    solvedMaterial?: IMaterialInfo,
  ): Observable<null> => {
    const questionStatus: IMaterialStatus = solutions?.length
      && this.data.question$.value?.header?.materialRelationId
      === solutions[0].materialRelationId
      ? this.data.question$.value.status
      : null;

    if (statusChanges?.length) {
      this.actions$.next({
        type: ActionType.TryAchieveStreak,
        requestType: RequestType.Parallel,
        data: {
          statusChanges,
          materialInfo: solvedMaterial,
        },
      });
      this.updateStatuses(statusChanges);
    }

    if (solutions?.length) {
      if (!solutions[0].isCorrect) {
        this.setFailedSolutionsCountValue();
      }
      this.actions$.next({
        type: ActionType.SubmitSolutions,
        requestType: RequestType.Parallel,
        data: {
          solutions,
          status: questionStatus,
        },
      });
    }

    if (heartsState) {
      this.actions$.next({
        type: ActionType.SetHeartsData,
        requestType: RequestType.Parallel,
        data: {
          hearts: heartsState,
        },
      });
    }

    return of(null);
  };

  public updateStatuses = (
    statusChanges: IMaterialStatus[],
  ): Observable<null> => {
    const { lesson$, course$, justStatusUpdatedCourse$ } = this.data;
    const lesson = lesson$.value;
    const course = course$.value;
    const statuses = [...statusChanges];

    if (lesson) {
      const updatedLesson = this.getStatusUpdatedMaterial(
        lesson,
        statuses,
        [MaterialType.Lesson],
      );
      lesson$.next(updatedLesson);
    }
    if (course && statuses.length) {
      const updatedCourse = this.getStatusUpdatedMaterial(
        course,
        statuses,
      );
      course$.next(updatedCourse);
      justStatusUpdatedCourse$.next(updatedCourse);
    }
    return of(null);
  };

  public makeMaterialChanges = (
    materialChanges: IMaterialChange[],
  ): Observable<null> => {
    const { lesson$, course$ } = this.data;
    const lesson = lesson$.value;
    const course = course$.value;

    if (lesson) {
      const updatedLesson = this.getChangedMaterial(
        lesson,
        materialChanges,
      );
      lesson$.next(updatedLesson);
    }
    if (course) {
      const updatedCourse = this.getChangedMaterial(
        course,
        materialChanges,
      );
      course$.next(updatedCourse);
    }
    return of(null);
  };

  private updateLastStatusChangedLessons = (
    material: IMaterial,
    status: IMaterialStatus,
  ) => {
    if (
      this.getParentMaterialType(
        material.materialInfo?.header?.materialRelationId,
        this.data.course$.value,
      ) !== MaterialType.Module
    ) {
      return;
    }

    if (material.materialInfo.status.completion !== status.completion) {
      this.data.lastCompletedMaterialIdInCurrentSession$.next(
        material.materialInfo.header.id,
      );
    }

    if (
      status.visibility === VisibilityStatus.Unlocked
      && material.materialInfo.status.visibility !== status.visibility
    ) {
      this.data.lastAvailableMaterialIdInCurrentSession$.next(
        material.materialInfo.header.id,
      );
    }
  };

  private getStatusUpdatedMaterial = (
    material: IMaterial,
    statuses: IMaterialStatus[],
    typesToKeep: MaterialType[] = [],
  ): IMaterial => {
    const updatedMaterial = material;
    const statusesCopy = statuses.map((status) => ({ ...status }));

    const statusIndex = statusesCopy?.findIndex(
      (s) => s.materialRelationId
        === material.materialInfo.header.materialRelationId,
    );
    if (statusIndex !== -1) {
      this.updateLastStatusChangedLessons(
        material,
        statusesCopy[statusIndex],
      );

      updatedMaterial.materialInfo.status = statusesCopy[statusIndex];
      if (
        !typesToKeep.includes(
          updatedMaterial.materialInfo.header.typeId,
        )
      ) {
        statusesCopy.splice(statusIndex, 1);
      }
    }

    if (statusesCopy.length && material.children?.length) {
      const updatedChildren = material.children.map((mat) => this.getStatusUpdatedMaterial(mat, statusesCopy));

      updatedMaterial.children = updatedChildren;
    }

    return updatedMaterial;
  };

  private getChangedMaterial = (
    material: IMaterial,
    materialChanges: IMaterialChange[],
  ): IMaterial => {
    const updatedMaterial = material;

    const currentMaterialChange = materialChanges.find(
      (mc) => mc.userMaterialId
        === material.materialInfo.header.materialRelationId,
    );

    if (currentMaterialChange) {
      updatedMaterial.materialInfo.requirementTypeId = currentMaterialChange.requirementTypeId;
      updatedMaterial.materialInfo.status.ownership = {
        isOwned: currentMaterialChange.isOwned,
        ownershipRequirementsTypeId:
          currentMaterialChange.ownershipRequirementsTypeId,
      };
    }

    if (material.children?.length) {
      const changedChildren = material.children.map((mat) => this.getChangedMaterial(mat, materialChanges));

      updatedMaterial.children = changedChildren;
    }

    return updatedMaterial;
  };

  public checkMaterialAvailability = (
    status: IMaterialStatus,
    relationId: number,
    requirementTypeId: MaterialRequirementType
  ): boolean => {
    if (!status.ownership.isOwned) {
      const isRequired = requirementTypeId !== MaterialRequirementType.Optional;
      const touchpoint = isRequired ? Touchpoint.blockedLearning : Touchpoint.codeCoach;
      this.paywallLoader.openPaywall({
        touchpoint, props: {}
      });
      return false;
    }
    if (
      status.availabilityTypeId === OwnershipAvailabilityType.Hearts
      && this.heart.isRanOutOfHearts()
    ) {
      this.actions$.next({
        type: ActionType.OpenHeartModal,
        requestType: RequestType.Parallel,
        data: {
          heartModalSource: HeartModalSource.LessonOpenFail,
          callback: () => {
            const { routeData$ } = this.data;
            history.push(
              `/learn/${routeData$.value.courseAlias
                ? `courses/${routeData$.value.courseAlias}`
                : `learning-paths/${routeData$.value.pathAlias}`
              }/code-coach/${relationId}?returnUrl=${location.pathname
              }${location.search}`,
            );
          },
        },
      });

      return false;
    }
    return true;
  };

  public openCodeRepo = (id: number): Observable<null> => {
    const { routeData$ } = this.data;

    history.push(
      `/learn/${routeData$.value.courseAlias
        ? `courses/${routeData$.value.courseAlias}`
        : `learning-paths/${routeData$.value.pathAlias}`
      }/code-repo/${id}?returnUrl=${location.pathname}${location.search}`,
    );
    return of(null);
  };

  public handleRunTIYClick = (
    id: number,
    status: IMaterialStatus,
  ): Observable<null> => {
    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnMaterialClick,
          relationId: id,
        },
      },
    });

    const { routeData$ } = this.data;
    const tiyUrl = `/learn/${routeData$.value.courseAlias
      ? `courses/${routeData$.value.courseAlias}`
      : `learning-paths/${routeData$.value.pathAlias}`
      }/tiy/${id}`;

    if (status.ownership.isOwned) {
      history.push(
        `${tiyUrl}?returnUrl=${location.pathname}${location.search}`,
      );
    } else {
      this.actions$.next({
        type: ActionType.NavigateToPro,
        requestType: RequestType.Parallel,
        data: {
          source: 'tiy',
          returnUrl: `${location.pathname}${location.search}&redirectUrl=${tiyUrl}`,
        },
      });
    }

    return of(null);
  };

  public openExternalResource = (
    externalResourceUrl: string,
    typeId: MaterialType,
    materialRelationId: number,
  ): Observable<null> => {
    this.data.externalResourceUrl$.next(externalResourceUrl);
    this.actions$.next({
      type: ActionType.SolveMaterial,
      requestType: RequestType.Cancelable,
      data: {
        solutions: [
          {
            typeId,
            materialRelationId,
          },
        ],
      },
    });

    return of(null);
  };

  public closeExternalResource = (): Observable<null> => {
    this.data.externalResourceUrl$.next(null);
    return of(null);
  };

  public openCodeCoach = (materialInfo: IMaterialInfo): Observable<null> => {
    const { routeData$ } = this.data;
    const {
      header: { typeId, materialRelationId },
      status,
      requirementTypeId
    } = materialInfo;
    const isOwned = this.checkMaterialAvailability;

    this.actions$.next({
      type: ActionType.Track,
      requestType: RequestType.Parallel,
      data: {
        trackData: {
          actionName: TrackActionName.OnMaterialClick,
          relationId: materialRelationId,
        },
      },
    });

    if (status.visibility === VisibilityStatus.Locked) {
      this.actions$.next({
        type: ActionType.OnLockedCardClick,
        requestType: RequestType.Parallel,
        data: {
          typeId,
        },
      });
      return of(null);
    }

    if (this.heart.isRanOutOfHearts()) {
      this.actions$.next({
        type: ActionType.OpenHeartModal,
        requestType: RequestType.Parallel,
        data: {
          heartModalSource: HeartModalSource.LessonOpenFail
        },
      });
      return of(null);
    }

    if (isOwned(status, materialRelationId, requirementTypeId)) {
      history.push(
        `/learn/${routeData$.value.courseAlias
          ? `courses/${routeData$.value.courseAlias}`
          : `learning-paths/${routeData$.value.pathAlias}`
        }/code-coach/${materialRelationId}?returnUrl=${location.pathname
        }${location.search}`,
      );
    }
    return of(null);
  };

  public openCodeProject = (
    id: number,
    returnUrl?: string,
  ): Observable<null> => {
    const { routeData$ } = this.data;

    history.push(
      `/learn/${routeData$.value.courseAlias
        ? `courses/${routeData$.value.courseAlias}`
        : `learning-paths/${routeData$.value.pathAlias}`
      }/code-project/${id}?returnUrl=${returnUrl || location.pathname + location.search
      }`,
    );
    return of(null);
  };

  private getParentMaterialType = (
    materialRelationId: number,
    material: IMaterial,
  ): MaterialType => {
    if (
      material?.children?.find(
        (child) => child.materialInfo?.header?.materialRelationId
          === materialRelationId,
      )
    ) {
      return material.materialInfo.header.typeId;
    }

    if (!material?.children?.length) {
      return null;
    }

    let materiaType: MaterialType = null;
    for (let i = 0; i < material?.children?.length; i++) {
      materiaType = this.getParentMaterialType(
        materialRelationId,
        material.children[i],
      );
      if (materiaType) break;
    }

    return materiaType;
  };
}
